import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useClientTheme } from "@/contexts/ThemeContext";

export default function ResetPassword() {
  const { t } = useTranslation();
  const { theme } = useClientTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: token.trim(), new_password: pwd });
      toast.success(t("auth.password_updated"));
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-5 py-10" data-theme={theme} data-testid="reset-root">
      <div className="absolute top-5 right-5 z-10 flex items-center gap-2">
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <h1 className="font-display text-4xl font-bold tracking-tight">{t("auth.reset_title")}</h1>
        <p className="text-zinc-400 mt-2 text-sm">{t("auth.reset_subtitle")}</p>

        <form onSubmit={onSubmit} className="mt-6 bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 space-y-4">
          <div>
            <Label className="text-zinc-300">{t("auth.reset_token")}</Label>
            <Input required value={token} onChange={(e) => setToken(e.target.value)}
                   className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 mt-1.5 font-mono text-xs"
                   data-testid="reset-token-input" />
          </div>
          <div>
            <Label className="text-zinc-300">{t("auth.reset_new_password")}</Label>
            <Input required type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                   className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 mt-1.5"
                   data-testid="reset-password-input" />
          </div>
          <Button type="submit" disabled={loading}
                  className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                  data-testid="reset-submit-button">
            {loading ? t("auth.reset_submitting") : t("auth.reset_submit")}
          </Button>

          <div className="text-center text-sm text-zinc-400">
            <Link to="/login" className="text-[#FFD700] hover:underline">{t("auth.reset_back")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
